import Button from "@/components/bricks/button/Button";
import { defaultPersonIdKortColumns } from "../../../../../config/person";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import {
  PersonIdKort,
  PersonIdKortStatus,
} from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import {
  formatIdKortTabelEksport,
  formatIdKortTabelRaekker,
  sortIdKortTabelRaekker,
} from "../../../../../util/person";
import { getHeaderActiveColumns, TD } from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import CheckIcon from "../../../../icons/CheckIcon";
import TableBasic, { TableRow } from "../../../../Table/TableBasic";
import IngenRegistreringer from "../IngenRegistreringer";
import PersonIdKortStatusBadge from "../PersonIdKortStatusBadge";
import useLocalStorageState from "use-local-storage-state";

/** Kun id'et bruges til at ekspedere bestillingen; resten af rækken er personsiden uvedkommende. */
export type MarkerIdKortSomSendt = (idKort: { id: string }) => void;

/**
 * Personens ID-kort-bestillinger som fanens fælles tabel-widget — sorterbare headere,
 * kolonnevælger, Hent-eksport og tom tilstand som nabosektionerne (ADV-236). Sektionen var
 * tidligere en liste; konsistens på tværs af "Mere om person" vejer tungere end at spare
 * kolonnemaskineriet for fire felter.
 *
 * Alle bestillinger vises, ikke kun den aktuelle: receptionen bruger historikken til at vurdere
 * en henvendelse om et manglende kort, og "har hun fået et kort før?" kan ikke besvares af det
 * nyeste kort alene.
 *
 * Rent præsentationslag — rækker, sortering og eksport-mapning ligger i util/person, hvor de kan
 * testes. Uden markerSomSendt (dvs. uden skriveadgang) findes handlingskolonnen slet ikke.
 */
const IdKortHistorik = ({
  historik,
  kundeNr,
  markerSomSendt,
}: {
  historik: PersonIdKort[];
  kundeNr: number;
  markerSomSendt?: MarkerIdKortSomSendt;
}) => {
  const [columns, setActiveColumns] = useActiveColumns(
    "person-personidkort-active-columns",
    defaultPersonIdKortColumns,
  );
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personIdKortSortBy",
    { defaultValue: defaultSortBy },
  );

  const raekker = sortIdKortTabelRaekker(
    formatIdKortTabelRaekker(historik),
    sortBy,
  );

  function downloadHandler() {
    tableToXLSX(columns, formatIdKortTabelEksport(raekker), {
      fileName: `id-kort-for-${kundeNr}`,
      additionalData: { kundenr: kundeNr },
    });
  }

  if (!historik.length) {
    return <IngenRegistreringer text="Ingen ID-kort bestillinger" />;
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <TableBasic
        defaultColumns={defaultPersonIdKortColumns}
        columns={columns}
        setActiveColumns={setActiveColumns}
        headerRow={getHeaderActiveColumns(columns, {
          defaultSortBy,
          setSortBy,
          sortBy,
        })}
      >
        {raekker.map((raekke) => {
          // Ikon-knap — samme greb som redigér-cellen i nabosektionerne. I sticky-kolonnen
          // fylder en tekstknap mere end den giver, og den bryder flugten med blyant-ikonerne.
          //
          // Teksten står KUN i aria-label. Knappens tooltip er vores egen (_buttons.scss:
          // .c-button--icon[aria-label]:after { content: attr(aria-label) }), så en title
          // ville lægge browserens grå boble oven i den sorte — to tooltips for én knap.
          const markerHandling =
            markerSomSendt && raekke.status === PersonIdKortStatus.Bestilt ? (
              <Button
                icon
                transparent
                color="primary"
                className="tooltip-center"
                aria-label="Markér som sendt"
                onClick={() => markerSomSendt(raekke)}
              >
                <CheckIcon height={16} />
              </Button>
            ) : null;

          return (
            <TableRow key={raekke.id} rowAction={markerHandling}>
              <>
                {columns
                  .filter((col) => col.active)
                  .map((col) =>
                    col.field === "status" ? (
                      <td key={col.field}>
                        <PersonIdKortStatusBadge status={raekke.status} />
                      </td>
                    ) : (
                      <TD key={col.field} col={col} rowData={raekke} />
                    ),
                  )}
              </>
            </TableRow>
          );
        })}
      </TableBasic>
    </>
  );
};

export default IdKortHistorik;

// Nyeste bestilling først: receptionen leder efter den, de netop har taget imod.
const defaultSortBy: SortBy[] = [{ direction: "desc", field: "opretDato" }];
